import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Search, ScrollText, Filter } from "lucide-react";

const ROLE_COLOR = { admin: "bg-orange/15 text-orange-dark", customer: "bg-blue-50 text-blue-700", professional: "bg-emerald-50 text-emerald-700", system: "bg-slate-100 text-slate-600" };

export default function AdminAudit() {
    const [rows, setRows] = useState(null);
    const [actions, setActions] = useState([]);
    const [action, setAction] = useState("");
    const [q, setQ] = useState("");

    const load = () => {
        setRows(null);
        const params = new URLSearchParams();
        if (action) params.set("action", action);
        if (q.trim()) params.set("q", q.trim());
        api.get(`/admin/audit?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    };

    useEffect(() => { api.get("/admin/audit/actions").then((r) => setActions(r.data)).catch(() => {}); }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [action]);

    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Audit Trail</h1>
            <p className="text-sm text-navy/55">Immutable log of estimates, payments, refunds, approvals, policy & admin changes.</p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 rounded-xl border border-navy/15 bg-white px-3">
                    <Filter size={15} className="text-navy/40" />
                    <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className="bg-transparent py-2.5 text-sm text-navy outline-none">
                        <option value="">All events</option>
                        {actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-1 items-center gap-2 rounded-xl border border-navy/15 bg-white px-3">
                    <Search size={15} className="text-navy/40" />
                    <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actor, target or details…" className="flex-1 bg-transparent py-2.5 text-sm text-navy outline-none" />
                </form>
                <button data-testid="audit-search-btn" onClick={load} className="rounded-xl bg-orange px-4 py-2.5 text-sm font-bold text-white">Search</button>
            </div>

            <div className="mt-4 overflow-hidden rounded-2xl border border-navy/10 bg-white">
                {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                {rows && rows.length === 0 && <div className="flex flex-col items-center py-16 text-navy/40"><ScrollText size={32} /><p className="mt-2 text-sm">No audit records found.</p></div>}
                {rows && rows.length > 0 && (
                    <div className="overflow-x-auto homiie-scroll">
                        <table className="w-full text-sm">
                            <thead className="bg-navy/[0.03] text-left text-xs uppercase tracking-wide text-navy/50">
                                <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Event</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Target</th><th className="px-4 py-3">Details</th></tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-t border-navy/5" data-testid="audit-row">
                                        <td className="whitespace-nowrap px-4 py-3 text-navy/50">{new Date(r.created_at).toLocaleString()}</td>
                                        <td className="px-4 py-3"><span className="rounded-full bg-navy/5 px-2.5 py-1 text-xs font-bold text-navy">{r.label}</span></td>
                                        <td className="px-4 py-3"><span className="text-navy/80">{r.actor_name}</span><span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${ROLE_COLOR[r.actor_role] || ROLE_COLOR.system}`}>{r.actor_role}</span></td>
                                        <td className="px-4 py-3 font-mono text-[11px] text-navy/50">{String(r.target || "—").slice(0, 10)}</td>
                                        <td className="px-4 py-3 text-xs text-navy/60">{r.meta && Object.keys(r.meta).length ? Object.entries(r.meta).map(([k, v]) => `${k}: ${v}`).join(" · ") : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {rows && rows.length > 0 && <p className="mt-2 text-xs text-navy/40">Showing {rows.length} most recent records.</p>}
        </div>
    );
}
